import type { DateRange } from '@/api/reportDateRanges';
import {
  dateRangesEqual,
  resolveWorkspaceDatePreset,
} from '@/api/reportDateRanges';
import { WORKSPACE_DATE_PRESETS } from '@/constants/Constant_Preset_Variables';
import type { CampaignAdProduct } from '@/types/spCampaigns';
import type {
  RuleAsinRow,
  RuleCampaignRow,
  RuleKeywordRow,
  RuleLogicChip,
  RuleRecommendationsData,
  RuleTableId,
  RuleTablePayload,
  RuleThresholds,
} from '@/types/ruleRecommendations';

export const DEFAULT_RULE_THRESHOLDS: RuleThresholds = {
  missedSpendMinRoas: 8,
  missedSpendMinOpportunity: 100,
  budgetUtilizationMin: 0.95,
  budgetIncreasePct: 20,
  tosImpressionFactor: 0.5,
  tosMaxShare: 50,
  poorKeywordMaxRoas: 2,
  poorKeywordMinClicks: 30,
  poorKeywordMaxOrders: 0,
  poorCampaignRoasFactor: 0.5,
  poorCampaignMinSpend: 100,
  chokedMinRating: 4,
  chokedMinUnits: 1,
};

export const RULE_TABLE_ORDER: RuleTableId[] = [
  'missedSpend',
  'lowTos',
  'poorKeywords',
  'poorCampaigns',
  'chokedAsins',
];

const TABLE_SEARCH_PLACEHOLDERS: Record<RuleTableId, string> = {
  missedSpend: 'Search campaigns…',
  lowTos: 'Search campaigns…',
  poorKeywords: 'Search keywords…',
  poorCampaigns: 'Search campaigns…',
  chokedAsins: 'Search ASINs…',
};

export function ruleTableSearchPlaceholder(tableId: RuleTableId): string {
  return TABLE_SEARCH_PLACEHOLDERS[tableId];
}

export function labelForDateRange(range: DateRange, now = new Date()): string {
  for (const preset of WORKSPACE_DATE_PRESETS) {
    const resolved = resolveWorkspaceDatePreset(preset.id, now);
    if (dateRangesEqual(range, resolved.current)) return preset.label;
  }
  return `${range.startDate} – ${range.endDate}`;
}

export function prefixLogicChipLabel(label: string, rangeLabel: string): string {
  if (!rangeLabel) return label;
  if (label.toLowerCase().includes(rangeLabel.toLowerCase())) return label;
  return `${rangeLabel} ${label}`;
}

export function formatIsoDisplayDate(iso: string | null | undefined, empty = 'No end date'): string {
  if (!iso) return empty;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return iso;
  return `${match[3]}-${match[2]}-${match[1]}`;
}

export function isSponsoredProducts(adProduct: string | undefined, campaignType?: string): boolean {
  const product = String(adProduct || '').toLowerCase();
  const type = String(campaignType || '').toUpperCase();
  return product === 'sponsored_products' || product === 'sp' || type === 'SP';
}

export function toShortAdProduct(adProduct: string | undefined, campaignType?: string): CampaignAdProduct {
  const product = String(adProduct || '').toLowerCase();
  const type = String(campaignType || '').toUpperCase();
  if (product === 'sponsored_brands' || product === 'sb' || type === 'SB') return 'SB';
  if (product === 'sponsored_display' || product === 'sd' || type === 'SD') return 'SD';
  return 'SP';
}

export function editorFieldsForTable(tableId: RuleTableId): Array<{
  key: keyof RuleThresholds;
  label: string;
  step: string;
}> {
  switch (tableId) {
    case 'missedSpend':
      return [
        { key: 'missedSpendMinRoas', label: 'Minimum ROAS', step: '0.1' },
        { key: 'missedSpendMinOpportunity', label: 'Minimum opportunity spend (₹)', step: '1' },
      ];
    case 'lowTos':
      return [
        { key: 'tosImpressionFactor', label: 'Impressions vs average (factor)', step: '0.1' },
        { key: 'tosMaxShare', label: 'Max TOS share (%)', step: '1' },
      ];
    case 'poorKeywords':
      return [
        { key: 'poorKeywordMaxRoas', label: 'Maximum ROAS', step: '0.1' },
        { key: 'poorKeywordMinClicks', label: 'Minimum clicks', step: '1' },
        { key: 'poorKeywordMaxOrders', label: 'Maximum orders', step: '1' },
      ];
    case 'poorCampaigns':
      return [
        { key: 'poorCampaignRoasFactor', label: 'ROAS vs Adv. ROAS (factor)', step: '0.1' },
        { key: 'poorCampaignMinSpend', label: 'Minimum ad spend (₹)', step: '1' },
      ];
    case 'chokedAsins':
      return [
        { key: 'chokedMinRating', label: 'Minimum rating', step: '0.1' },
        { key: 'chokedMinUnits', label: 'Minimum units sold', step: '1' },
      ];
    default:
      return [];
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function asNumberOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = asNumber(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function parseChips(value: unknown): RuleLogicChip[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const rec = asRecord(item);
      const key = asString(rec.key);
      const label = asString(rec.label);
      if (!key || !label) return null;
      const tooltip = asString(rec.tooltip);
      return tooltip ? { key, label, tooltip } : { key, label };
    })
    .filter((item): item is RuleLogicChip => item != null);
}

function parseCampaignRow(value: unknown): RuleCampaignRow | null {
  const rec = asRecord(value);
  const campaignId = asString(rec.campaignId);
  if (!campaignId) return null;
  return {
    adProduct: asString(rec.adProduct, 'sponsored_products'),
    campaignType: asString(rec.campaignType, 'SP'),
    campaignId,
    campaignName: asString(rec.campaignName, campaignId),
    state: asString(rec.state, 'ENABLED'),
    targetingType: rec.targetingType == null ? null : asString(rec.targetingType),
    startDate: rec.startDate == null ? null : asString(rec.startDate),
    endDate: rec.endDate == null ? null : asString(rec.endDate),
    impressions: asNumber(rec.impressions),
    clicks: asNumber(rec.clicks),
    cost: asNumber(rec.cost),
    sales30d: asNumber(rec.sales30d),
    purchases30d: asNumber(rec.purchases30d),
    roas: asNumber(rec.roas),
    acos: asNumberOrNull(rec.acos),
    ctr: asNumber(rec.ctr),
    dailyBudget: asNumberOrNull(rec.dailyBudget),
    budgetType: asString(rec.budgetType, 'DAILY'),
    biddingStrategy: rec.biddingStrategy == null ? null : asString(rec.biddingStrategy),
    biddingStrategyLabel: asString(rec.biddingStrategyLabel, 'Unknown'),
    tosBidPercent: asNumberOrNull(rec.tosBidPercent),
    suggestedBudget: asNumberOrNull(rec.suggestedBudget),
    opportunitySpend: asNumberOrNull(rec.opportunitySpend) ?? undefined,
    opportunitySales: asNumberOrNull(rec.opportunitySales) ?? undefined,
    budgetIncreasePct: asNumberOrNull(rec.budgetIncreasePct) ?? undefined,
    projectedAdditionalCost: asNumberOrNull(rec.projectedAdditionalCost) ?? undefined,
    projectedAdditionalSales: asNumberOrNull(rec.projectedAdditionalSales) ?? undefined,
    projectedAdditionalEarnings: asNumberOrNull(rec.projectedAdditionalEarnings) ?? undefined,
    projectedSavings: asNumberOrNull(rec.projectedSavings) ?? undefined,
    projectedLossAvoided: asNumberOrNull(rec.projectedLossAvoided) ?? undefined,
    tosShare: asNumberOrNull(rec.tosShare),
    tosRoas: asNumberOrNull(rec.tosRoas),
    tosCtr: asNumberOrNull(rec.tosCtr),
  };
}

function parseKeywordRow(value: unknown): RuleKeywordRow | null {
  const rec = asRecord(value);
  const keywordId = asString(rec.keywordId);
  if (!keywordId) return null;
  return {
    adProduct: asString(rec.adProduct, 'sponsored_products'),
    keywordId,
    keywordText: asString(rec.keywordText),
    matchType: asString(rec.matchType),
    campaignId: asString(rec.campaignId),
    campaignName: asString(rec.campaignName),
    adGroupId: asString(rec.adGroupId),
    state: asString(rec.state, 'ENABLED'),
    currentBid: asNumber(rec.currentBid),
    recommendedBid: asNumber(rec.recommendedBid),
    bidDecreasePct: asNumber(rec.bidDecreasePct, 20),
    impressions: asNumber(rec.impressions),
    clicks: asNumber(rec.clicks),
    cost: asNumber(rec.cost),
    sales30d: asNumber(rec.sales30d),
    purchases30d: asNumber(rec.purchases30d),
    roas: asNumber(rec.roas),
    acos: asNumberOrNull(rec.acos),
  };
}

function parseAsinRow(value: unknown): RuleAsinRow | null {
  const rec = asRecord(value);
  const asin = asString(rec.asin);
  if (!asin) return null;
  return {
    asin,
    productName: asString(rec.productName, asin),
    adProduct: asString(rec.adProduct, 'sponsored_products'),
    campaignType: asString(rec.campaignType, 'SP'),
    campaignId: asString(rec.campaignId),
    campaignName: asString(rec.campaignName),
    startDate: rec.startDate == null ? null : asString(rec.startDate),
    endDate: rec.endDate == null ? null : asString(rec.endDate),
    dailyBudget: asNumberOrNull(rec.dailyBudget),
    budgetType: asString(rec.budgetType, 'DAILY'),
    suggestedBudget: asNumberOrNull(rec.suggestedBudget),
    cost: asNumber(rec.cost),
    sales30d: asNumber(rec.sales30d),
    purchases30d: asNumber(rec.purchases30d),
    unitsSold: asNumber(rec.unitsSold),
    roas: asNumber(rec.roas),
    cvr: asNumber(rec.cvr),
    rating: asNumberOrNull(rec.rating),
    budgetIncreasePct: asNumberOrNull(rec.budgetIncreasePct) ?? undefined,
  };
}

function parseTable<T>(
  value: unknown,
  id: RuleTableId,
  parseRow: (item: unknown) => T | null
): RuleTablePayload<T> {
  const rec = asRecord(value);
  const rows = Array.isArray(rec.rows)
    ? rec.rows.map(parseRow).filter((row): row is T => row != null)
    : [];
  return {
    id,
    title: asString(rec.title) || id,
    logic: parseChips(rec.logic),
    rows,
    rowCount: asNumber(rec.rowCount, rows.length),
    note: rec.note == null ? undefined : asString(rec.note),
    ratingsAvailable: typeof rec.ratingsAvailable === 'boolean' ? rec.ratingsAvailable : undefined,
  };
}

export function parseRuleRecommendations(data: unknown): RuleRecommendationsData {
  const rec = asRecord(data);
  const tables = asRecord(rec.tables);
  const thresholds = asRecord(rec.thresholds);
  const benchmarks = asRecord(rec.benchmarks);
  return {
    range: asRecord(rec.range) as { startDate?: string; endDate?: string },
    noData: Boolean(rec.noData),
    benchmarks: {
      advRoas: asNumber(benchmarks.advRoas),
      avgImpressions: asNumber(benchmarks.avgImpressions),
    },
    thresholds: {
      missedSpendMinRoas: asNumberOrNull(thresholds.missedSpendMinRoas) ?? undefined,
      missedSpendMinOpportunity: asNumberOrNull(thresholds.missedSpendMinOpportunity) ?? undefined,
      budgetUtilizationMin: asNumberOrNull(thresholds.budgetUtilizationMin) ?? undefined,
      budgetIncreasePct: asNumberOrNull(thresholds.budgetIncreasePct) ?? undefined,
      tosImpressionFactor: asNumberOrNull(thresholds.tosImpressionFactor) ?? undefined,
      tosMaxShare: asNumberOrNull(thresholds.tosMaxShare) ?? undefined,
      poorKeywordMaxRoas: asNumberOrNull(thresholds.poorKeywordMaxRoas) ?? undefined,
      poorKeywordMinClicks: asNumberOrNull(thresholds.poorKeywordMinClicks) ?? undefined,
      poorKeywordMaxOrders: asNumberOrNull(thresholds.poorKeywordMaxOrders) ?? undefined,
      poorCampaignRoasFactor: asNumberOrNull(thresholds.poorCampaignRoasFactor) ?? undefined,
      poorCampaignMinSpend: asNumberOrNull(thresholds.poorCampaignMinSpend) ?? undefined,
      chokedMinRating: asNumberOrNull(thresholds.chokedMinRating) ?? undefined,
      chokedMinUnits: asNumberOrNull(thresholds.chokedMinUnits) ?? undefined,
    },
    tables: {
      missedSpend: parseTable(tables.missedSpend, 'missedSpend', parseCampaignRow),
      lowTos: parseTable(tables.lowTos, 'lowTos', parseCampaignRow),
      poorKeywords: parseTable(tables.poorKeywords, 'poorKeywords', parseKeywordRow),
      poorCampaigns: parseTable(tables.poorCampaigns, 'poorCampaigns', parseCampaignRow),
      chokedAsins: parseTable(tables.chokedAsins, 'chokedAsins', parseAsinRow),
    },
  };
}
